import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ActionText, Box, Flex, HoverBlock } from "../../../components/Flex";
import { Content, parseDescription } from "../../../components/Layout";
import { FilledLabel, Label, Paragraph, Title } from "../../../components/Text";
import { Button, TextButton } from "../../../components/Button";
import { CloseIcon } from "../../../icons/CloseIcon";
import { VerifiedIcon } from "../../../icons/VerifiedIcon";
import { RevokeIcon } from "../../../icons/RevokeIcon";
import { DoneIcon } from "../../../icons/DoneIcon";
import { RemoveIcon } from "../../../icons/RemoveIcon";
import { EditIcon } from "../../../icons/EditIcon";
import { EditDetailsIcon } from "../../../icons/EditDetailsIcon";
import { DownloadIcon } from "../../../icons/DownloadIcon";
import { fixPackageSize, formatDate, numberWithSpaces } from "../../../utils";
import { Scrollbar } from "../../../components/Scrollbar";
import { filter, find, findIndex, size, toLower } from "lodash";
import { KITSTYLE, SWTYPES } from "../../../settings";
import { UploadProgressRow } from "../../uploads/UploadProgressRow";
import { ExternalLinkIcon } from "../../../icons/ExternalLinkIcon";
import { PlusIcon } from "../../../icons/PlusIcon";
import { Modal } from "../../../components/Modal";
import { Badge } from "../../../components/Circle";
import { CancelVerificationForm } from "./CancelVerificationForm";
import { DocumentationForm } from "./DocumentationForm";
import { AuditIcon } from "../../../icons/AuditIcon";


const PkgDetails = ({
    data,
    loading,
    verifyPackage,
    removePackage,
    archivePackage,
    editInfo,
    downloadPackage,
    uploadProgress,
    contractsWrite,
    packageWrite,
    isAdmin,
    adminRead,
    onClose,
    addDocLink,
    editDocLink,
    removeDocLink,
    renderFieldContent
}) => {

    const initialState = { visible: false, values: {} };
    const [ state, setState ] = useState(initialState);

    const restrictions = {
        ecokits: "Rights to Ecokits",
        rToMinorRel: "Rights to Minor releases"
    };

    const fields = {
        info: [
            { label: "Product name", attr: "product", renderUndefined: false},
            { label: "Version", attr: "version" },
            { label: "System Architecture", attr: "sysarch", render: (d) => (
                <Flex gap={{ right: 8 }}>
                    {Array.isArray(d)
                        ? d.map(sysarch => (
                            <FilledLabel key={sysarch}>{sysarch}</FilledLabel>
                        ))
                        : d
                    }
                </Flex>
            )},
            { label: "OS Version", attr: "minos", render: (d) => (
                <Flex gap={{ right: 8 }} wrap="wrap" style={{ margin: "-2px"}}>
                    {Array.isArray(d)
                        ? d.map(minos => (
                            <FilledLabel key={minos} style={{ margin: "2px"}}>{minos}</FilledLabel>
                        ))
                        : d
                    }
                </Flex>
            )},
            { label: "Kit Style", attr: "style", render: (d) => find(KITSTYLE, { key: d.toLowerCase() }) ? find(KITSTYLE, { key: d.toLowerCase() }).title  : null },
            { label: "Kit Type", attr: "swtype", render: (d, record) => find(SWTYPES, { key: d.toLowerCase() }) ? find(SWTYPES, { key: d.toLowerCase() }).title + (record.ecoRating ? ` (Rating-${record.ecoRating})` : "") : null },
            { label: "Restart required", attr: "restart", render: d => d == 1 ? "Yes" : "No" },
            adminRead && { label: "Registered Date", attr: "registeredDatetime", render: (d) => formatDate(d, "DD-MMM-YYYY HH:mm:ss") },
            { label: "Release Date", attr: "releaseDate", render: (d) => formatDate(d, "DD-MMM-YYYY") },
            adminRead && { label: "Registered By", attr: "registeredBy" },
            adminRead && { label: "Verified By", attr: "verifiedBy" },
            adminRead && { label: "Comment on verification", attr: "verifyComment" },
            { label: "Description", attr: "description", render: (d) => <Flex direction="column" gap={{ bottom: 2 }}>
                {typeof d === "string"
                    ? parseDescription(d)
                    : d}
                </Flex>}
        ],
        file: [
            { label: "File Size", attr: "size", render: (text, record) => record.uploaded ? `${fixPackageSize(text)} (${numberWithSpaces(text)} bytes)` : "-" },
            { label: "MD5 Checksum", attr: "md5", render: (text, record) => record.uploaded ? text : "-" },
            adminRead && { label: "Uploaded", attr: "uploaded", render: (d) => formatDate(d, "DD-MMM-YYYY HH:mm:ss") },
        ]
    };

    const wrapToLink = (packageNumber, packageName, hasAccess) => {
        let li = <>&mdash; {packageName}</>
        if (hasAccess) {
            return <Link key={packageNumber} to={`/packages/${toLower(packageNumber)}`}>{li}</Link>
        }

        return <span key={packageNumber}>{li}</span>;
    };


    return(
        <>
        <Content background="#FFFFFF" gap={{ bottom: 18 }}>
            <Flex width="100%" justify="space-between" margin={{ bottom: "24px"}}>
                <Flex align="center" gap={{ right: 8 }}>
                    {adminRead
                        ? <Badge size={12} color={data.verified == 1 ? "var(--sp-success-color)" : "var(--sp-error-color)"}/>
                        : data.downloaded ? <VerifiedIcon size={16} title="You've already downloaded the package." /> : <Badge size={10} color="var(--sp-success-color)" />}
                    <Title h4 isLoading={loading} color={!adminRead && data.downloaded ? "var(--sp-success-color)" : null}>
                        {data.name}
                    </Title>
                    {data.archived && <FilledLabel filled>archived</FilledLabel>}
                </Flex>
                <CloseIcon onClick={onClose} />
            </Flex>
            <Flex height="calc(100% - 108px)" justify="space-between" gap={{ right: 18 }}>
                <Scrollbar>
                    <Flex direction="column" gap={{ bottom: 8 }}>
                        {renderFieldContent(fields.info, data)}
                        {Array.isArray(data.docs) && data.docs.length
                            ? <Flex direction="column" gap={{ bottom: 8 }}>
                                <Label textTransform="capitalize" size={22}>Documentation:</Label>
                                {data.docs.map(doc => (
                                    <Flex key={doc.id} gap={{ right: 22 }} align="center">
                                        <a key={doc.id} href={doc.link} target="_blank">
                                            <Flex align="center" gap={{ right: 8 }}>
                                                <ExternalLinkIcon size={16} />
                                                <span>{doc.description}</span>
                                            </Flex>
                                        </a>
                                        {packageWrite &&
                                        <HoverBlock>
                                            <Flex gap={{ right: 12 }} align="center">
                                                <ActionText onClick={() => setState({ visible: "docs", values: find(data.docs, { id: doc.id }) })}><EditIcon size={20} /></ActionText>
                                                <ActionText onClick={() => removeDocLink(doc)}><RemoveIcon size={20} hoverColor="var(--sp-error-hover-color)" small /></ActionText>
                                            </Flex>
                                        </HoverBlock>}
                                    </Flex>
                                ))}
                            </Flex>
                            : null}
                        {packageWrite && <ActionText onClick={() => setState({ visible: "docs", values: {} })} gap={{ right: 16 }}><PlusIcon /><span>Add link to documentation</span></ActionText>}
                    </Flex>
                </Scrollbar>
                <Flex flex="0 0 450px">
                    <Scrollbar>
                        <Flex direction="column" gap={{ bottom: 19 }} padding={{ right: "19px" }}>
                            <Box flatten background="#F0F4FB" gap={{ bottom: 8 }} style={{ position: "relative" }}>
                                <Title h6 capitalize>Package Files</Title>
                                    {Array.isArray(data.files)
                                        ? <Flex direction="column" gap={{ bottom: 16 }}>
                                            {data.files.map(file => (
                                                uploadProgress[file.key]
                                                    ? <UploadProgressRow
                                                        key={file.key}
                                                        name={file.saveAs}
                                                        progress={uploadProgress[file.key].progress}
                                                        status={uploadProgress[file.key].status}
                                                    />
                                                    : <Flex direction="column" align="flex-start" gap={{ right: 8, bottom: 8 }} key={file.key}>
                                                        <TextButton
                                                            disabled={loading || !file.uploaded}
                                                            maxWidth="390px"
                                                            style={{ fontWeight: 700 }}
                                                            onClick={() => downloadPackage({ key: file.key, minos: data.minos })}
                                                        >
                                                            &mdash; {file.saveAs}
                                                        </TextButton>
                                                        {renderFieldContent(fields.file, file)}
                                                    </Flex>
                                            ))}
                                        </Flex>
                                        : null}
                            </Box>
                            <Box flatten background="#F0F4FB" gap={{ bottom: 8 }}>
                                <Flex justify="space-between" align="center">
                                    <Title h6 capitalize>package permissions</Title>
                                    {contractsWrite && <TextButton  onClick={() => !loading && editInfo("restrictions", { key: data.key, contracts: data.contracts, ...data.restrictions })}>
                                        <span><EditDetailsIcon /></span>
                                        <span>Edit</span>
                                    </TextButton>}
                                </Flex>
                                {Array.isArray(data.contracts)
                                    ? data.contracts.map((contract, idx) => (
                                        <Paragraph key={`pkg-contract-${idx}`}>
                                            &mdash; {contract.name}
                                        </Paragraph>
                                    ))
                                    : null}
                                {Object.keys(data.restrictions || {}).map(key => (
                                    data.restrictions[key]
                                    ? <Flex key={key}>&mdash; {restrictions[key]}</Flex>
                                    : null
                                ))}
                            </Box>
                            <Box flatten background="#F0F4FB" gap={{ bottom: 8 }} margin={{ bottom: "19px" }}>
                                <Flex justify="space-between" align="center">
                                    <Title h6 capitalize>minimum package requirements</Title>
                                    {packageWrite &&<TextButton onClick={() => !loading && editInfo("prereq", { key: data.key, prereq: data.prereq, sysarch: data.sysarch })}>
                                        <span><EditDetailsIcon /></span>
                                        <span>Edit</span>
                                    </TextButton>}
                                </Flex>
                                {Array.isArray(data.prereq)
                                    ? data.prereq.map((pr) => wrapToLink(pr.packageNumber, pr.name, pr.hasAccess))
                                    : null}
                            </Box>
                            <Box flatten background="#F0F4FB" gap={{ bottom: 8 }}>
                                <Title h6 capitalize>required for</Title>
                                {Array.isArray(data.requiredFor)
                                    ? data.requiredFor.map((pr) => wrapToLink(pr.packageNumber, pr.name, pr.hasAccess))
                                    : null}
                            </Box>
                        </Flex>
                    </Scrollbar>
                </Flex>
            </Flex>
            <Flex justify="space-between">
                <Flex gap={{ right: 12 }}>
                    {packageWrite &&
                    <>
                        <Button onClick={() => editInfo("packages", data)} disabled={loading || size(uploadProgress) || data.verified == 1}>
                            <EditIcon size={20} />
                            Edit Package
                        </Button>
                        <Button onClick={() => data.verified == 1 ? setState({ visible: "verify", values: {} }) : verifyPackage({ id: data.key, verify: 1 })} disabled={loading || size(uploadProgress) || findIndex(data.files, { uploaded: null }) > -1 || !(Array.isArray(data.files) && data.files.length)}>
                            {data.verified == 1 ? <RevokeIcon size={20} /> : <DoneIcon size={20} />}
                            {data.verified == 1 ? "Undo Verify" : "Verify"}
                        </Button>
                    </>}
                    <Button
                        onClick={() => filter(data.files, file => file.uploaded !== null).map(file => downloadPackage({ key: file.key, minos: data.minos }))}
                        disabled={loading || size(uploadProgress) || (Array.isArray(data.files) && data.files.length ? filter(data.files, { uploaded: null }).length == data.files.length : true)}
                    >
                        <DownloadIcon size={20} />
                        Download
                    </Button>
                    {isAdmin &&
                    <>
                        <Button
                            onClick={() => archivePackage(data)}
                            disabled={loading}
                        >
                            <AuditIcon size={20} />
                            {data.archived ? "Restore" : "Archive"}
                        </Button>
                        <Button
                            danger
                            onClick={() => removePackage({
                                content: <Paragraph>Are you sure you want to delete <b>{data.name}</b>?</Paragraph>,
                                id: data.key
                            })}
                            disabled={loading || (find(uploadProgress, { status: 3 })) || data.verified == 1}
                        >
                            <RemoveIcon size={20} />
                            Delete
                        </Button>
                    </>}
                </Flex>
            </Flex>
        </Content>
        <Modal visible={["docs", "verify"].includes(state.visible)} onCancel={() => setState(initialState)}>
            {state.visible == "docs"
                ? <DocumentationForm
                    initialValues={state.values}
                    onCancel={() => setState(initialState)}
                    onSubmit={(values) => { size(state.values) ? editDocLink(values) : addDocLink(values); setState(initialState); }}
                />
                : <CancelVerificationForm
                    onCancel={() => setState(initialState)}
                    onSubmit={(comment) => verifyPackage({ id: data.key, verify: 0, comment: comment })}
                />}

        </Modal>
        </>
    );
};

export { PkgDetails };